import { Howl } from "howler"
import * as Sentry from "@sentry/react"

const globalAudioCache = new Map()

const activeAudios = new Set()
let nativeSrcDescriptor = null

let sentryWarningSent = false
let sentryErrorSent = false

const isDev = process.env.REACT_APP_ENVIORNMENT === "development"

const isStaticAsset = (src) => {
  if (!src) return false
  return src.includes("/static/media/") || src.startsWith("data:audio/")
}

const logDebug = (msg, style) => {
  if (isDev) {
    if (style) console.log(msg, style)
    else console.log(msg)
  }
}

const cleanupAudio = (audio) => {
  if (!audio) return
  try {
    audio.pause()
    audio.src = ""
    audio.removeAttribute("src")
    audio.load()
  } catch (e) {}
}

export const clearAllSounds = () => {
  logDebug(
    `%c[Audio Cache] Flushing all audio engine registries completely...`,
    "color: #F44336; font-weight: bold; font-size: 12px;"
  )

  for (const [, sound] of globalAudioCache.entries()) {
    try {
      sound.off()
      sound.unload()
    } catch (e) {}
  }
  globalAudioCache.clear()

  for (const audio of [...activeAudios]) {
    try {
      cleanupAudio(audio)
    } catch (e) {}
  }
  activeAudios.clear()
}

export const clearCurrentExerciseSoundsOnly = () => {
  logDebug(
    `%c[Audio Cache] Executing selective exclusion cleanup for static assets...`,
    "color: #FF9800; font-weight: bold; font-size: 12px;"
  )

  for (const [src, sound] of globalAudioCache.entries()) {
    if (src && !isStaticAsset(src)) {
      try {
        sound.off()
        sound.unload()
        globalAudioCache.delete(src)
      } catch (e) {}
    }
  }

  for (const audio of [...activeAudios]) {
    try {
      const src = nativeSrcDescriptor?.get?.call(audio) || ""
      if (src && !isStaticAsset(src)) {
        cleanupAudio(audio)
        activeAudios.delete(audio)
      }
    } catch (e) {}
  }
}

export const registerAudioElement = (audio) => {
  if (!audio || activeAudios.has(audio)) return
  audio.__debugId =
    audio.__debugId || Math.random().toString(36).substring(2, 9)
  audio.__creationUrl = window.location.href
  audio.__isReactManaged = true
  activeAudios.add(audio)

  logDebug(
    `%c[Audio Cache] Registered React-managed audio: ${
      audio.src?.split("/").pop() || "(no src)"
    }`,
    "color: #4CAF50; font-weight: bold;"
  )
}

export const unregisterAudioElement = (audio) => {
  if (!audio) return
  activeAudios.delete(audio)

  logDebug(
    `%c[Audio Cache] Unregistered audio element`,
    "color: #FF9800; font-weight: bold;"
  )
}

export const getOrCreateSound = (src, options = {}) => {
  if (!src) return null
  if (globalAudioCache.has(src)) return globalAudioCache.get(src)
  return new Howl({ src: [src], ...options })
}

export const initAudioTracking = () => {
  if (typeof window === "undefined") return

  try {
    nativeSrcDescriptor = Object.getOwnPropertyDescriptor(
      HTMLMediaElement.prototype,
      "src"
    )

    if (typeof Howl !== "undefined" && Howl.prototype) {
      const originalInit = Howl.prototype.init
      Howl.prototype.init = function (o) {
        const src = o?.src?.[0] || ""

        if (src && globalAudioCache.has(src)) {
          const cachedHowl = globalAudioCache.get(src)

          if (isStaticAsset(src) && cachedHowl.state() === "unloaded") {
            logDebug(
              `%c[Use-Sound Refresh] Re-creating fresh instance for static audio: ${src
                .split("/")
                .pop()}`,
              "color: #00BCD4; font-weight: bold;"
            )
            globalAudioCache.delete(src)
          } else if (cachedHowl.state() === "unloaded") {
            globalAudioCache.delete(src)
          } else {
            logDebug(
              `%c[Anti-Duplication] Reusing global singleton instance for lesson asset: ${src
                .split("/")
                .pop()}`,
              "color: #2196F3; font-weight: bold;"
            )
            Object.assign(this, cachedHowl)
            this.play = cachedHowl.play.bind(cachedHowl)
            this.stop = cachedHowl.stop.bind(cachedHowl)
            this.unload = cachedHowl.unload.bind(cachedHowl)
            this.state = cachedHowl.state.bind(cachedHowl)
            return this
          }
        }

        this.__creationUrl = window.location.href
        this.__originalOptions = o

        const res = originalInit.apply(this, arguments)

        if (src && !globalAudioCache.has(src)) {
          globalAudioCache.set(src, this)
        }
        return res
      }

      const originalPlay = Howl.prototype.play
      Howl.prototype.play = function () {
        const src = this._src || this.__originalOptions?.src?.[0] || ""

        if (src && isStaticAsset(src) && this.state() === "unloaded") {
          logDebug(
            `%c[Auto-Repair] Resurrecting WebAudio node context for: ${src
              .split("/")
              .pop()}`,
            "color: #FF5722; font-weight: bold; text-transform: uppercase;"
          )
          originalInit.call(this, this.__originalOptions || { src: [src] })
          globalAudioCache.set(src, this)
        }

        return originalPlay.apply(this, arguments)
      }

      const originalUnload = Howl.prototype.unload
      Howl.prototype.unload = function () {
        if (this._sounds) {
          this._sounds.forEach((s) => {
            if (s._node) cleanupAudio(s._node)
          })
        }
        if (this._node) cleanupAudio(this._node)

        for (const [key, value] of globalAudioCache.entries()) {
          if (value === this) globalAudioCache.delete(key)
        }
        return originalUnload.apply(this, arguments)
      }
    }

    const trackAudio = (audio) => {
      if (activeAudios.has(audio)) return

      const id = Math.random().toString(36).substring(2, 9)
      audio.__debugId = id
      audio.__creationUrl = window.location.href

      activeAudios.add(audio)

      if (activeAudios.size === 65 && !sentryWarningSent) {
        sentryWarningSent = true
        Sentry.captureMessage(
          "High number of active audio elements (approaching WebMediaPlayer limit)",
          {
            level: "warning",
            tags: { issue: "WebMediaPlayer_limit_warning" },
            extra: {
              count: activeAudios.size,
              urls: Array.from(activeAudios)
                .map((a) => nativeSrcDescriptor?.get?.call(a))
                .filter(Boolean)
                .slice(-20),
            },
          }
        )
      }
    }

    const OriginalAudio = typeof Audio !== "undefined" ? window.Audio : null
    if (OriginalAudio) {
      window.Audio = function (...args) {
        try {
          const audio = new OriginalAudio(...args)
          trackAudio(audio)
          return audio
        } catch (error) {
          if (
            error?.message?.includes("WebMediaPlayer") &&
            error?.message?.includes("already in existence")
          ) {
            if (!sentryErrorSent) {
              sentryErrorSent = true
              Sentry.captureException(error, {
                tags: { issue: "WebMediaPlayer_limit_reached" },
                extra: { count: activeAudios.size },
              })
            }
          }
          throw error
        }
      }
      window.Audio.prototype = OriginalAudio.prototype
    }

    const originalCreateElement = document.createElement
    document.createElement = function (tagName, options) {
      if (!tagName || tagName.toLowerCase() !== "audio") {
        return originalCreateElement.call(this, tagName, options)
      }

      try {
        const el = originalCreateElement.call(this, tagName, options)
        trackAudio(el)
        return el
      } catch (error) {
        if (
          error?.message?.includes("WebMediaPlayer") &&
          error?.message?.includes("already in existence")
        ) {
          if (!sentryErrorSent) {
            sentryErrorSent = true
            Sentry.captureException(error, {
              tags: { issue: "WebMediaPlayer_limit_reached" },
              extra: { count: activeAudios.size },
            })
          }
        }
        throw error
      }
    }

    window.getActiveAudios = () => {
      const seenSrcs = new Set()
      const list = []

      const currentParams = new URLSearchParams(window.location.search)
      const currentExerciseId = currentParams.get("exerciseId") || ""

      for (const audio of activeAudios) {
        try {
          const src = nativeSrcDescriptor?.get?.call(audio) || ""

          if (src === "" || src === "null") {
            activeAudios.delete(audio)
            continue
          }

          if (
            !isStaticAsset(src) &&
            audio.__creationUrl &&
            currentExerciseId !== ""
          ) {
            const creationUrlParams = new URLSearchParams(
              audio.__creationUrl.split("?")[1] || ""
            )
            const creationExerciseId = creationUrlParams.get("exerciseId") || ""

            if (
              creationExerciseId !== "" &&
              creationExerciseId !== currentExerciseId
            ) {
              activeAudios.delete(audio)
              continue
            }
          }

          const key = src || `__react_${audio.__debugId}`
          if (seenSrcs.has(key)) continue
          seenSrcs.add(key)

          list.push({
            id: audio.__debugId || "player-node",
            audioSrc: src || "(react-audioplayer)",
            appUrl: window.location.href,
            element: {
              __debugId: audio.__debugId || "player-node",
              __creationUrl: audio.__creationUrl || window.location.href,
              _unlocked: true,
            },
          })
        } catch (e) {
          activeAudios.delete(audio)
        }
      }
      return list
    }

    window.getAudioCount = () => window.getActiveAudios()
  } catch (err) {
    console.warn("[AudioManager] Failed to initialize audio tracking:", err)
  }
}
